/**
 * UpdatePassword
 *
 * Verifies Firebase password reset code and lets the user set a new password
 * with basic validation and clear success/error flows.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { auth } from '../../../firebase/firebase';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import useDarkMode from '../../../hooks/useDarkMode';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import Footer from '../../../components/shared/Footer';

export default function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [email, setEmail] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDark = useDarkMode();

  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  const toggleTheme = () => {
    // This will be handled by the useDarkMode hook
  };

  const handleNavigation = () => {
    // Handle any navigation logic if needed
  };

  // Verify the password reset code when component mounts
  useEffect(() => {
    const verifyCode = async () => {
      // Handle both old Firebase template format (mode=action) and correct format (mode=resetPassword)
      if (!oobCode || (mode !== 'resetPassword' && mode !== 'action')) {
        setError('Invalid or missing password reset link. Please request a new password reset.');
        setIsVerifying(false);
        return;
      }

      try {
        setIsVerifying(true);
        // Verify the password reset code and get the associated email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        console.log('Password reset code verified for email:', userEmail);
      } catch (err) {
        console.error('Error verifying password reset code:', err);
        let errorMessage = 'Invalid or expired password reset link.';
        
        if (err.code === 'auth/expired-action-code') {
          errorMessage = 'This password reset link has expired. Please request a new password reset.';
        } else if (err.code === 'auth/invalid-action-code') {
          errorMessage = 'This password reset link is invalid or has already been used. Please request a new password reset.';
        }
        
        setError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long.';
    }
    // Add more validation rules if needed
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      // Confirm the password reset with the new password
      await confirmPasswordReset(auth, oobCode, password);
      setSuccessMessage('Password updated successfully! You can now sign in with your new password.');
      
      // Clear form
      setPassword('');
      setConfirmPassword('');
      
      // Redirect to sign in page after 3 seconds
      setTimeout(() => {
        navigate('/signin', { 
          state: { 
            message: 'Password updated successfully! Please sign in with your new password.' 
          }
        });
      }, 3000);
      
    } catch (err) {
      console.error('Error resetting password:', err);
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (err.code === 'auth/expired-action-code') {
        errorMessage = 'This password reset link has expired. Please request a new password reset.';
      } else if (err.code === 'auth/invalid-action-code') {
        errorMessage = 'This password reset link is invalid or has already been used. Please request a new password reset.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Use the SimpleLandingHeader component */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/signin"
        backButtonText="Back to sign in"
        onNavigation={handleNavigation}
        showLogo={true}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">Update Password</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Set your new password
            </p>
            {email && (
              <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
                Enter a new password for <span className="font-semibold">{email}</span>
              </p>
            )}
          </div>
        </div>

        {/* Loading verification */}
        {isVerifying && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700 dark:text-blue-200">Verifying password reset link...</p>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
              </div>
            </div>
            {(error.includes('expired') || error.includes('invalid')) && (
              <div className="mt-3 ml-8">
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-semibold text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 underline"
                >
                  Request a new password reset
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-200">{successMessage}</p>
                <p className="text-xs text-green-600 dark:text-green-300 mt-1">Redirecting to sign in page...</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {!isVerifying && !error && !successMessage && (
          <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] px-6 py-8 mt-6 sm:px-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                >
                  New Password
                </label>
                <div className="mt-2 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 pr-10 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Password must be at least 6 characters long.
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                >
                  Confirm New Password
                </label>
                <div className="mt-2 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 pr-10 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className={`flex w-full justify-center items-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3.5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 ${isLoading || !password || !confirmPassword ? 'opacity-95 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>

              <div className="text-center">
                <Link 
                  to="/signin" 
                  className="text-sm font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </div>
        )}

        {/* If there's an error and no form, show alternative actions */}
        {!isVerifying && error && !successMessage && (
          <div className="text-center mt-8">
            <div className="space-y-4">
              <Link 
                to="/forgot-password" 
                className="inline-flex items-center px-4 py-2 border border-purple-600 text-sm font-medium rounded-md text-purple-600 bg-white hover:bg-purple-50 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-gray-700 transition-colors"
              >
                Request New Password Reset
              </Link>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                or
              </div>
              <Link 
                to="/signin" 
                className="text-sm font-semibold text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
} 